import React, { useState, useRef, useCallback } from 'react'
import { SendMessageData } from '../services/chatterService'

interface MessageInputProps {
  onSendMessage: (messageData: SendMessageData) => Promise<void>
  disabled?: boolean
  placeholder?: string
}

const MessageInput: React.FC<MessageInputProps> = ({
  onSendMessage,
  disabled = false,
  placeholder = "Type your message..."
}) => {
  const [message, setMessage] = useState('')
  const [files, setFiles] = useState<File[]>([])
  const [isDragging, setIsDragging] = useState(false)
  const [sending, setSending] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // Supported file types
  const supportedTypes = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'image/png',
    'image/jpeg',
    'image/jpg'
  ]

  const validateFile = (file: File): string | null => {
    if (!supportedTypes.includes(file.type)) {
      return `File type ${file.type} is not supported. Please upload PDF, DOC, DOCX, PNG, JPG, or JPEG files.`
    }
    
    if (file.size > 10 * 1024 * 1024) { // 10MB limit
      return `File ${file.name} is too large. Maximum file size is 10MB.`
    }
    
    return null
  }

  const handleFileSelect = (selectedFiles: FileList | null) => {
    if (!selectedFiles) return

    const newFiles: File[] = []
    const errors: string[] = []

    Array.from(selectedFiles).forEach(file => {
      const error = validateFile(file)
      if (error) {
        errors.push(error)
      } else {
        newFiles.push(file)
      }
    })

    if (errors.length > 0) {
      alert(errors.join('\n'))
      return
    }

    // Limit to 5 files total
    const totalFiles = [...files, ...newFiles]
    if (totalFiles.length > 5) {
      alert('Maximum 5 files allowed per message')
      return
    }

    setFiles(totalFiles)
  }

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    handleFileSelect(e.dataTransfer.files)
  }, [files])

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if ((!message.trim() && files.length === 0) || sending || disabled) {
      return
    }

    try {
      setSending(true)
      
      const messageData: SendMessageData = {
        message: message.trim(),
        messageType: files.length > 0 ? 'file_upload' : 'text',
        files: files.length > 0 ? files : undefined
      }

      await onSendMessage(messageData)
      
      // Clear form on success
      setMessage('')
      setFiles([])
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
      
      // Focus back to textarea
      textareaRef.current?.focus()
    } catch (error) {
      console.error('Error sending message:', error)
      // Error handling is done in the parent component
    } finally {
      setSending(false)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit(e as any)
    }
  }

  const getFileIcon = (file: File) => {
    if (file.type.includes('pdf')) return '📄'
    if (file.type.includes('image')) return '🖼️'
    if (file.type.includes('word') || file.type.includes('document')) return '📝'
    return '📎'
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  return (
    <div className="message-input-container">
      {/* File attachments preview */}
      {files.length > 0 && (
        <div className="file-attachments-preview">
          {files.map((file, index) => (
            <div key={index} className="attachment-preview">
              <span className="file-icon">{getFileIcon(file)}</span>
              <div className="file-info">
                <span className="file-name">{file.name}</span>
                <span className="file-size">{formatFileSize(file.size)}</span>
              </div>
              <button
                type="button"
                onClick={() => removeFile(index)}
                className="remove-file"
                aria-label="Remove file"
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}

      <form onSubmit={handleSubmit} className="message-input-form">
        <div 
          className={`input-area ${isDragging ? 'dragging' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <textarea
            ref={textareaRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={isDragging ? "Drop files here..." : placeholder}
            disabled={disabled || sending}
            rows={1}
            className="message-textarea"
          />
          
          <div className="input-actions">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
              onChange={(e) => handleFileSelect(e.target.files)}
              style={{ display: 'none' }}
            />
            
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={disabled || sending}
              className="attach-file-btn"
              title="Attach files (PDF, DOC, DOCX, PNG, JPG)"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66L9.64 16.2a2 2 0 0 1-2.83-2.83l8.49-8.49"/>
              </svg>
            </button>
            
            <button
              type="submit"
              disabled={(!message.trim() && files.length === 0) || disabled || sending}
              className="send-message-btn"
            >
              {sending ? 'Sending...' : 'Send Message'}
            </button>
          </div>
        </div>
        
        {isDragging && (
          <div className="drag-overlay">
            <div className="drag-message">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: '8px'}}>
                <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66L9.64 16.2a2 2 0 0 1-2.83-2.83l8.49-8.49"/>
              </svg>
              Drop files here to attach
            </div>
          </div>
        )}
      </form>
    </div>
  )
}

export default MessageInput
